import type { JsonRecord, PageResult, Session } from "./types";

let csrfToken: string | undefined;

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function firstString(record: JsonRecord, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function camelizeRecord<T>(value: unknown): T {
  if (Array.isArray(value)) return value.map((item) => camelizeRecord(item)) as T;
  if (!isRecord(value)) return value as T;
  const result: JsonRecord = {};
  for (const [key, item] of Object.entries(value)) {
    result[key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())] = camelizeRecord(item);
  }
  return result as T;
}

export function setCsrfToken(token?: string) {
  csrfToken = token;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("x-admin-csrf", csrfToken);
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });

  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    const record = isRecord(payload) ? payload : {};
    throw new ApiError(
      firstString(record, "message", "error") ?? `Request failed with status ${response.status}`,
      response.status,
      payload,
    );
  }

  return camelizeRecord<T>(payload);
}

export function normalizeSession(payload: unknown): Session {
  const data = camelizeRecord<JsonRecord>(payload);
  const nested = isRecord(data.session) ? data.session : undefined;
  const source = nested ?? data;
  const admin = isRecord(source.admin)
    ? source.admin
    : isRecord(source.user)
      ? source.user
      : isRecord(data.admin)
        ? data.admin
        : undefined;
  const token = firstString(source, "csrfToken", "csrf") ?? firstString(data, "csrfToken", "csrf");
  const authenticated =
    source.authenticated === true ||
    data.authenticated === true ||
    Boolean(admin && typeof admin.id === "string");
  const session: Session = {
    authenticated,
    environment: firstString(source, "environment") ?? firstString(data, "environment"),
    csrfToken: token,
    expiresAt: (source.expiresAt ?? data.expiresAt) as number | string | null | undefined,
    admin: admin as unknown as Session["admin"],
  };
  setCsrfToken(token);
  return session;
}

export function unwrapRecord<T>(payload: unknown, ...keys: string[]): T {
  const data = camelizeRecord<unknown>(payload);
  if (!isRecord(data)) return data as T;
  if (isRecord(data.data)) {
    for (const key of keys) {
      if (data.data[key] !== undefined) return data.data[key] as T;
    }
  }
  for (const key of keys) {
    if (data[key] !== undefined) return data[key] as T;
  }
  return data as T;
}

export function unwrapPage<T>(payload: unknown, key: string, fallbackPage = 1): PageResult<T> {
  const normalized = camelizeRecord<unknown>(payload);
  const data = isRecord(normalized) && isRecord(normalized.data) ? normalized.data : normalized;
  if (Array.isArray(data)) {
    return { items: data as T[], page: fallbackPage, pageSize: data.length || 25, total: data.length, pages: 1 };
  }
  if (!isRecord(data)) {
    return { items: [], page: fallbackPage, pageSize: 25, total: 0, pages: 1 };
  }
  const candidate = data[key] ?? data.items ?? data.results;
  const items = Array.isArray(candidate) ? (candidate as T[]) : [];
  const page = typeof data.page === "number" ? data.page : fallbackPage;
  const pageSize = typeof data.pageSize === "number" ? data.pageSize : Math.max(items.length, 25);
  const total = typeof data.total === "number" ? data.total : items.length;
  const pages = typeof data.pages === "number" ? data.pages : Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return { items, page, pageSize, total, pages };
}

export function toQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export function formatDate(value?: number | string | null, withTime = false): string {
  if (value === undefined || value === null || value === "") return "Not available";
  let date: Date;
  if (typeof value === "number") date = new Date(value < 10_000_000_000 ? value * 1000 : value);
  else {
    const numeric = Number(value);
    date = Number.isFinite(numeric) && String(numeric) === String(value)
      ? new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric)
      : new Date(value);
  }
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat(undefined, withTime
    ? { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" }).format(date);
}

export function relativeDate(value?: number | string | null): string {
  if (value === undefined || value === null) return "Never";
  const numeric = typeof value === "number" ? value : Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 10_000_000_000 ? numeric * 1000 : numeric)
    : new Date(String(value));
  if (Number.isNaN(date.valueOf())) return String(value);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < 60) return formatter.format(seconds, "second");
  if (abs < 3600) return formatter.format(Math.round(seconds / 60), "minute");
  if (abs < 86_400) return formatter.format(Math.round(seconds / 3600), "hour");
  if (abs < 2_592_000) return formatter.format(Math.round(seconds / 86_400), "day");
  return formatter.format(Math.round(seconds / 2_592_000), "month");
}

export function formatNumber(value?: number | null): string {
  return new Intl.NumberFormat(undefined, { notation: value && Math.abs(value) > 9999 ? "compact" : "standard" }).format(value ?? 0);
}

export function formatBytes(value?: number | null): string {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

export function initials(name?: string | null, email?: string | null): string {
  const value = name?.trim() || email?.split("@")[0] || "?";
  const parts = value.split(/[\s._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "?";
}
